import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { AfterViewInit, Component, OnInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { CheckboxChangeEvent } from 'primeng/checkbox';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ApiConstants } from 'src/app/base/api-constants';
import { AppLoaderService } from 'src/app/services/app-loader.service';
import { UtilService } from 'src/app/services/util.service';
import { updateAppDM } from 'src/app/STORE/actions/applicationdatamodel.actions';
import { appDMFailData, appDMSuccData } from 'src/app/STORE/selectors/applicationdatamodel.selector';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { HttpBaseService } from 'src/app/base/http-base.service';

export interface TRtFieldConfigIF {
  shared_fields?: string[];
  statusField?: string | null;
  value_override?: string[];
  visibility_override?: string[];
  businessRuleEvents?: string[];
}

interface FormDataIF {
  t_runtime_workflow: boolean;
  type: string;
  t_rt_field_configuration: TRtFieldConfigIF;
  name: string;
  orgId: string;
  sid: string;
  appId: string;
  hasWorkflow: boolean;
  tableId: string;
  valueOverride: any[];
  visibilityOverride: string[];
  sharedFields: string[];
  statusField: string;
  hasBusinessRules: boolean;
  businessRules: string[];
}

interface PayloadFormDataIF {
  orgId: string;
  sid: string;
  appId: string;
  hasWorkflow: boolean;
  tableId: string;
  valueOverride: any[];
  visibilityOverride: string[];
  sharedFields: string[];
  statusField: string;
  hasBusinessRules: boolean;
  businessRules: string[];
}

interface TableDataIF {
  name: string;
  sid: string;
  fieldShared: boolean;
  overrideFieldProperty: boolean;
  overrideFieldValue: boolean;
  isSystemGeneratedField?: boolean;
  isPrimaryKeyField?: boolean;
}

@Component({
  selector: 'vb-runtime-field-config-dialog',
  templateUrl: './runtime-field-config-dialog.component.html',
  styleUrls: ['./runtime-field-config-dialog.component.scss'],
  standalone: false  //NOSONAR
})
export class RuntimeFieldConfigDialogComponent implements OnInit, AfterViewInit {
  private confirmationDialogService = inject(ConfirmationDialogService);
  private dialogRef = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private store = inject(Store);
  private loader = inject(AppLoaderService);
  private translateService = inject(TranslateService);
  private util = inject(UtilService);
  private appGlobalService = inject(AppGlobalService);
  private httpBaseService = inject(HttpBaseService);

  formData: FormDataIF;
  tableData: TableDataIF[] = [];
  tableDataFilterResults: TableDataIF[] = [];
  isClearSearchHidden = true;
  searchString = '';
  selectDeselectAllFieldsSharedState = false;
  selectDeselectAllOverridePropertiesState = false;
  selectDeselectAllOverrideValuesState = false;
  primaryKeyFields: string[] = [];
  disableField = false;
  isSaveInProgress = false;
  workflowStatusField: any;
  isConfigDirty: boolean = false;
  workFlowStatusFieldSid: string = '';
  appId: string = '';

  errorMsg: string = '';
  sharedFieldErrorMsg: string = '';
  visibilityOverrideErrorMsg: string = '';
  valueOverrideErrorMsg: string = '';

  ngOnInit(): void {
    this.appId = this.appGlobalService.get('appId');
    this.formData = { ...this.config.data.formData };
    this.disableField = this.config.data.disableField;

    // t_rt_field_configuration will JSON when sent from store, since it is passed as JSON string in the PUT /updateFields. So parse it.
    this.formData.t_rt_field_configuration =
      typeof this.config.data.formData.t_rt_field_configuration === 'string'
        ? JSON.parse(this.config.data.formData.t_rt_field_configuration)
        : this.getDefaultFieldConfig();

    this.workflowStatusField = this.config.data.pickList.allFieldsWithInfo.find(
      (field) => field.sid === this.config.data.formData.statusField
    );

    this.workFlowStatusFieldSid = this.config.data.formData.statusField;
    this.constructTableData();
    this.validateRuntimeConfig();
  }

  ngAfterViewInit(): void {
    this.updateSelectAllState();
  }

  errorArray: any[] = [];

  showPopup: string = '';

  toggleWarningPopup(sid: string): void {
    if (!this.showPopup) {
      this.showPopup = sid;
    } else {
      this.showPopup = '';
    }
  }

  isWarningVisible(sid: string): boolean {
    return this.showPopup === sid;
  }

  getErrorMessage(sid: number): string {
    const error = this.errorArray.find((e) => e.id === sid);
    return error ? error.message : '';
  }

  validateRuntimeConfig() {
    const allFields = this.config.data.pickList.allFieldsWithInfo;
    const sidSet = new Set(allFields.map((f) => f.sid));
    const fieldsUsedInRuntime = Array.from(
      new Set([
        ...(this.formData.sharedFields || []),
        ...(this.formData.visibilityOverride || []),
        ...(this.formData.valueOverride || [])
      ])
    );

    fieldsUsedInRuntime.forEach((field) => {
      if (!sidSet.has(field)) {
        this.util.handleDeletedIds([field], false, '', false, 'runtimeFieldDeleted', (message) => {
          this.errorArray.push({ id: field, type: 'no-field', message: message });
        });
      } else {
        const type = allFields.find((f) => f.sid === field)?.f_type;

        if (type === 'Image Carousel' || type === 'File Attachments' || type === 'Color') {
          this.util.handleDeletedIds([field], false, '', false, 'runtimeFieldTypeMismatch', (message) => {
            this.errorArray.push({ id: field, type: 'type-mismatch', message: message });
          });
        }
      }
    });
  }

  /**
   * It returns an object of type FieldConfigurationIF with default values
   * @returns {FieldConfigurationIF}
   */
  getDefaultFieldConfig(): TRtFieldConfigIF {
    return {
      shared_fields: [],
      visibility_override: [],
      value_override: [],
      statusField: '',
      businessRuleEvents: []
    };
  }

  /**
   * constructs the array of objects for rendering and storng the table rows and data
   */
  constructTableData() {
    const fieldsMap = new Map();

    // System generated fields.
    this.config.data.pickList.allFieldsWithInfo.forEach((field) => {
      fieldsMap.set(field.sid, field);
      if (field.sysGen && !field.f_aField) {
        this.tableData.push({
          name: field.name,
          sid: field.sid,
          fieldShared: this.formData.sharedFields?.includes(field.sid),
          overrideFieldProperty: false,
          overrideFieldValue: false,
          isSystemGeneratedField: true,
          isPrimaryKeyField: this.primaryKeyFields.indexOf(field.sid) !== -1
        });
      }
    });
    // User generated fields without Img and File Attachment types
    this.config.data.pickList.noImgFAFields.forEach((field) => {
      const fieldData = fieldsMap.get(field.sid);
      if (
        fieldData?.f_transientField !== true &&
        fieldData?.f_type !== 'Color' &&
        fieldData?.f_type !== 'Image Carousel' &&
        fieldData?.f_type !== 'File Attachments'
      ) {
        this.tableData.push({
          name: field.name,
          sid: field.sid,
          fieldShared: field.sid == this.workFlowStatusFieldSid || this.formData.sharedFields?.includes(field.sid),
          overrideFieldProperty:
            field.sid == this.workFlowStatusFieldSid || this.formData.visibilityOverride?.includes(field.sid),
          overrideFieldValue:
            field.sid == this.workFlowStatusFieldSid || this.formData.valueOverride?.includes(field.sid),
          isPrimaryKeyField: this.primaryKeyFields.indexOf(field.sid) !== -1 ? true : false
        });
      }
    });
    this.tableDataFilterResults = this.tableData;
  }

  /**
   * Filter the table based on search query
   * @param {any} event - Input event recieved on search query input
   */
  filterTableData(event) {
    if (this.searchString !== '') {
      this.tableDataFilterResults = this.tableData.filter((data) => {
        return data.name.toLowerCase().includes(this.searchString.toLowerCase());
      });
      this.updateSelectAllState();
    }
    if ((event && event.which === $.ui.keyCode.ESCAPE) || $.trim(this.searchString) === '') {
      this.clearSearch();
    } else {
      this.isClearSearchHidden = false;
    }
  }

  /**
   * Checks the override properties when shared field unchecked
   * @param {TableDataIF} field Table row data
   */
  onFieldSharedClick(field: TableDataIF, event: CheckboxChangeEvent) {
    this.isConfigDirty = true;
    field.fieldShared = event.checked;
    if (!field.fieldShared) {
      field.overrideFieldProperty = false;
      field.overrideFieldValue = false;
    }
    this.updateSelectAllState();
  }

  /**
   * Checks the shared field on checking the either of the override field
   * @param {TableDataIF} field Table row data
   */
  onOverrideFieldPropertyClick(field: TableDataIF, event: CheckboxChangeEvent) {
    this.isConfigDirty = true;
    field.overrideFieldProperty = event.checked;
    this.updateSelectAllState();
  }

  /**
   * Checks the shared field on checking the either of the override field
   * @param {TableDataIF} field Table row data
   */
  onOverrideFieldValueClick(field: TableDataIF, event: CheckboxChangeEvent) {
    this.isConfigDirty = true;
    field.overrideFieldValue = event.checked;
    this.updateSelectAllState();
  }

  /**
   * To close the dialog
   */
  closeDialog(dirtyCheck = true) {
    if (dirtyCheck && this.isConfigDirty) {
      this.confirmationDialogService.confirm({
        message: this.translateService.instant('UN_SAVED_CUD'),
        accept: () => {
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
    }
  }

  /**
   * To clear the search query and update table data
   */
  clearSearch() {
    this.isClearSearchHidden = true;
    this.searchString = '';
    this.tableDataFilterResults = this.tableData;
    this.updateSelectAllState();
  }

  getUpdatedFormData(): any {
    let runtimeConfigFormData: TRtFieldConfigIF = { ...this.formData.t_rt_field_configuration };
    let updatedFormData: any = {
      sharedFields: [],
      visibilityOverride: [],
      valueOverride: [],
      hasWorkflow: this.config.data.formData.hasWorkflow,
      hadWorkflow: this.config.data.formData.hadWorkflow,
      hasBusinessRules: this.config.data.formData.hasBusinessRules,
      businessRules: this.config.data.formData.businessRules,
      orgId: this.config.data.formData.orgId,
      sid: this.config.data.formData.sid,
      tableId: this.config.data.formData.sid,
      statusField: this.config.data.formData.statusField,
      appId: this.appId
    };

    this.tableData.forEach((fieldData) => {
      if (fieldData.fieldShared) {
        updatedFormData.sharedFields.push(fieldData.sid);
      }
      if (fieldData.overrideFieldProperty) {
        updatedFormData.visibilityOverride.push(fieldData.sid);
      }
      if (fieldData.overrideFieldValue) {
        updatedFormData.valueOverride.push(fieldData.sid);
      }
    });

    return updatedFormData;
  }

  /**
   * Gets the payload and saves the data
   */
  save() {
    this.loader.show();
    this.isSaveInProgress = true;
    let updatedFormData: any = this.getUpdatedFormData();
    const runtimeConfigSub = this.httpBaseService
      .put({ ...ApiConstants.updateRuntimeConfig }, updatedFormData)
      .subscribe(
        (res: any) => {
          this.loader.hide();
          this.isSaveInProgress = false;
          this.closeDialog(false);
          this.util.showMessage('success', this.translateService.instant('RT_FIELD_CONF_SAVED'));
          runtimeConfigSub.unsubscribe();
        },
        (error) => {
          this.loader.hide();
          this.isSaveInProgress = false;
          this.closeDialog(false);
          runtimeConfigSub.unsubscribe();
        }
      );
  }

  selectDesectAllFieldsShared(event: CheckboxChangeEvent) {
    this.tableDataFilterResults.forEach((field) => {
      if (field.sid !== this.workflowStatusField?.sid) {
        this.onFieldSharedClick(field, event);
      }
    });
    this.updateSelectAllState();
  }

  selectDesectAllOverrideProperties(event: CheckboxChangeEvent) {
    this.tableDataFilterResults.forEach((field) => {
      if (field.sid !== this.workflowStatusField?.sid && !field.isSystemGeneratedField && field.fieldShared) {
        this.onOverrideFieldPropertyClick(field, event);
      }
    });
  }

  selectDesectAllOverrideValues(event: CheckboxChangeEvent) {
    this.tableDataFilterResults.forEach((field) => {
      if (
        field.sid !== this.workflowStatusField?.sid &&
        !field.isSystemGeneratedField &&
        !field.isPrimaryKeyField &&
        field.fieldShared
      ) {
        this.onOverrideFieldValueClick(field, event);
      }
    });
  }

  updateSelectAllState() {
    if (this.tableDataFilterResults.length) {
      this.selectDeselectAllFieldsSharedState =
        this.selectDeselectAllOverridePropertiesState =
        this.selectDeselectAllOverrideValuesState =
          false;

      const tableState = {
        shared: { eligibleFields: 0, checkedField: 0 },
        overrideProperty: { eligibleFields: 0, checkedField: 0 },
        overrideValue: { eligibleFields: 0, checkedField: 0 }
      };

      this.tableDataFilterResults.forEach((field) => {
        tableState.shared.eligibleFields += 1;
        if (field.fieldShared) {
          tableState.shared.checkedField += 1;

          if (!field.isSystemGeneratedField) {
            tableState.overrideProperty.eligibleFields += 1;
            if (field.overrideFieldProperty) {
              tableState.overrideProperty.checkedField += 1;
            }

            if (!field.isPrimaryKeyField) {
              tableState.overrideValue.eligibleFields += 1;
              if (field.overrideFieldValue) {
                tableState.overrideValue.checkedField += 1;
              }
            }
          }
        }
      });

      this.selectDeselectAllFieldsSharedState = !!(
        tableState.shared.eligibleFields !== 0 && tableState.shared.eligibleFields === tableState.shared.checkedField
      );
      this.selectDeselectAllOverridePropertiesState = !!(
        tableState.overrideProperty.eligibleFields !== 0 &&
        tableState.overrideProperty.eligibleFields === tableState.overrideProperty.checkedField
      );
      this.selectDeselectAllOverrideValuesState = !!(
        tableState.overrideValue.eligibleFields !== 0 &&
        tableState.overrideValue.eligibleFields === tableState.overrideValue.checkedField
      );
    } else {
      this.selectDeselectAllFieldsSharedState =
        this.selectDeselectAllOverridePropertiesState =
        this.selectDeselectAllOverrideValuesState =
          false;
    }
  }

  getFieldClass(fieldSid: string) {
    const error = this.errorArray.find((err) => err.id === fieldSid);
    if (!error) return '';
    if (error.type === 'no-field') return 'error-icon1';
    if (error.type === 'type-mismatch') return 'warning-icon';
    return '';
  }

  hasWarning(fieldSid: string) {
    const error = this.errorArray.find((err) => err.id === fieldSid && err.type === 'type-mismatch');
    if (error) return true;
    return false;
  }

  hasError(fieldSid: string) {
    const error = this.errorArray.find((err) => err.id === fieldSid && err.type === 'no-field');
    if (error) return true;
    return false;
  }
}


